
import React from 'react';
import { ChevronDown, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { motion } from 'framer-motion';

export interface FilterOption {
  id: string;
  label: string;
  checked: boolean;
}

interface FilterDropdownProps {
  label?: string;
  options: FilterOption[];
  onFilterChange: (updatedOptions: FilterOption[]) => void;
  className?: string;
  icon?: React.ReactNode;
  hideCount?: boolean; // Prop to hide the count badge
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label = "Filters",
  options,
  onFilterChange,
  className,
  icon = <Filter size={16} className="text-blue-600 dark:text-blue-400" />,
  hideCount = false
}) => {
  const handleItemClick = (id: string) => {
    const updatedOptions = options.map(option => 
      option.id === id ? { ...option, checked: !option.checked } : option
    );
    onFilterChange(updatedOptions);
  };

  const activeFiltersCount = options.filter(option => option.checked).length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className={cn(
            "relative px-4 py-2 h-10 rounded-md text-sm font-medium",
            "bg-white/30 dark:bg-white/10 backdrop-blur-sm",
            "border border-accent/20 focus:border-accent/40",
            "text-slate-700 dark:text-slate-200",
            "shadow-sm hover:shadow-md transition-all duration-300",
            "flex items-center gap-2 transform-gpu",
            className
          )}
        >
          {icon}
          <span>{label}</span>
          {!hideCount && activeFiltersCount > 0 && (
            <span className="flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-gradient-to-br from-[var(--brand-blue-gradient-from)] to-[var(--brand-blue-gradient-to)] rounded-full">
              {activeFiltersCount}
            </span>
          )}
          <ChevronDown size={16} className="ml-1 text-blue-600 dark:text-blue-400" />
        </motion.button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-56 bg-white/80 dark:bg-black/80 backdrop-blur-md border border-accent/20"
        sideOffset={4}
      >
        <DropdownMenuLabel className="text-slate-700 dark:text-slate-200 px-4">Filter Options</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-accent/10" />
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.id}
            checked={option.checked}
            onCheckedChange={() => handleItemClick(option.id)}
            className="text-slate-700 dark:text-slate-200 focus:text-slate-700 dark:focus:text-slate-200 hover:text-slate-700 dark:hover:text-slate-200 focus:bg-accent/10 hover:bg-accent/10 px-4"
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default FilterDropdown;
